import { ArrowLeft, ArrowRight, Cross, Star } from '@components/icons'
import { CLOSE_PANEL } from '@components/utils/textVariables'
import { Dialog, Transition } from '@headlessui/react'
import Link from 'next/link'
import { Fragment, useEffect, useRef, useState } from 'react'
import ReviewInput from '../Reviews/ReviewInput'
import { groupBy } from '@framework/utils/lodash'
import { useUI } from '@components/ui'
import { isEmpty } from '@framework/utils/lodash'
import { ALLOWED_SLUG_FOR_TROOPOD, DATE_FORMAT, EmptyGuid, FirebaseKeys } from '@components/utils/constants'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import { getRemoteConfigAsBoolean, openLoginView } from '@framework/utils/app-util'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { formatDate } from '@components/utils/dateFormatter'
import Router, { useRouter } from 'next/router'
import { IProduct } from '@components/utils/model/product.model'
import { IDeviceInfo } from '@components/ui/context'
import { NEXT_PUBLIC_CUSTOMER_REVIEW_IMAGES_DISABLED } from '../../utils/constants'
import TroopodEvent from '@components/troopod/TroopodEvent'
import { IMAGE_ASSET_BASE_URL } from '@framework/utils/constants'

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface IProps {
  product: IProduct
  deviceInfo: IDeviceInfo
  reviews: any
}

export default function RatingsAndReview({
  product,
  deviceInfo,
  reviews: reviewsData,
}: IProps) {
  const trooRatingRef = useRef(null);
  const reviews = reviewsData.productRating
  const [isSubmitReview, setSubmitReview] = useState(false)
  const [isAllReview, setAllReview] = useState(3)
  const reviewImages = reviews?.productReviews
    ? groupBy(reviews.productReviews, 'postedBy')
    : undefined
  const { user, setSidebarView } = useUI()
  const { isMobile, isIPadorTablet } = deviceInfo
  const router = useRouter()
  const { asPath } = useRouter()

  function readWriteReview(isWrite?: boolean) {
    recordGA4Event(window, GA_EVENT.REVIEW_ACTION, {
      action_type: isWrite ? 'write a review' : 'read review',
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })

    if (isWrite) {
      Router.push(
        `/rate/product?stockCode=${product?.stockCode}&ref=${router?.asPath}`
      )
    }
  }

  if (!reviews?.productReviews?.length) {
    return null
  }
  const isTroopod = getRemoteConfigAsBoolean(FirebaseKeys.TROOPOD_ENABLED)
  return (
    <>
      <div className="flex flex-col">
        <div className="section-devider"></div>
      </div>
      <div className="px-0 mx-auto sm:container rating-container page-container sm:px-6">
        <section className="flex flex-col px-4 sm:px-0">
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-12">
            <div className="sm:col-span-4">
              <div className="sticky flex flex-col bg-white top-32">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-dark-brown">
                    Ratings &amp; Reviews
                  </h2>
                  {reviews?.productReviews?.length > 0 ? (
                    <>
                      <p className="text-sm font-normal text-brown">
                        Based on {reviews?.totalRecord} Customer Reviews
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-normal text-brown">
                        Be the first to review this product.
                      </p>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-1 sm:grid-cols-1">
                  <div className="sm:col-span-1">
                    {reviews != null ? (
                      <>
                        <h3 className="text-6xl font-bold text-dark-brown sm:text-8xl">
                          <span>
                            {parseFloat(
                              reviews?.ratingAverage.toString()
                            ).toFixed(1)}
                          </span>
                          <span className="inline-block text-3xl font-normal text-gray-400 sm:text-5xl">
                            /5
                          </span>
                        </h3>
                      </>
                    ) : (
                      <>
                        <h3 className="text-6xl font-bold text-dark-brown sm:text-8xl">
                          {/* <span>No Ratings</span> */}
                          <span className="inline-block text-3xl font-normal text-gray-400 sm:text-5xl">
                            No Ratings
                          </span>
                        </h3>
                      </>
                    )}
                    <p className="my-2 ratings">
                      {[0, 1, 2, 3, 4].map((rating, iid: number) => (
                        <Star
                          key={`product-${iid}-new`}
                          className={classNames(
                            reviews?.ratingAverage > rating
                              ? 'text-orange-500'
                              : 'text-gray-200',
                            'sm:h-14 sm:w-14 h-7 w-7 flex-shrink-0 inline-block ipad-h-w'
                          )}
                          aria-hidden="true"
                        />
                      ))}
                    </p>
                  </div>
                  <div className="sm:col-span-1">
                    <p className="mt-4 text-sm font-normal text-brown">
                      Purchased this product?
                    </p>
                    {!isEmpty(user) ||
                    (user?.userId && user?.userId != EmptyGuid) ? (
                      <>
                        <button
                          type="button"
                          onClick={() => readWriteReview(true)}
                          className="px-4 py-3 mt-3 text-sm font-semibold text-orange-500 bg-white border border-orange-400"
                        >
                          Write a Review
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            openLoginView(asPath)
                            setSidebarView('LOGIN_VIEW')
                          }}
                          className="px-4 py-3 mt-3 text-sm font-semibold text-orange-500 bg-white border border-orange-400"
                        >
                          Write a Review
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="sm:col-span-8">
              {product?.link && (isMobile || isIPadorTablet) &&
              ALLOWED_SLUG_FOR_TROOPOD.includes(product?.link) && isTroopod && (
                <>
                  <div
                    ref={trooRatingRef}
                    className="troo trooFeed mobileOnly"
                    data-productId={product?.recordId || product?.productId}
                    data-tag="secondary"
                    data-title="Ratings &amp; Reviews"
                  ></div>
                <TroopodEvent elemRef={trooRatingRef} />
                </>
              )}
              {/* CUSTOMER REVIEW IMAGES */}
              {!NEXT_PUBLIC_CUSTOMER_REVIEW_IMAGES_DISABLED &&
                reviewImages?.null?.length &&
                reviewImages?.null?.length > 0 && (
                  <>
                    <div className="flex flex-col">
                      <h3 className="mb-2 text-sm font-semibold text-secondary-full-opacity">
                        Customer Images
                      </h3>
                      {isMobile || isIPadorTablet ? (
                        <Swiper
                          slidesPerView={5.5}
                          spaceBetween={10}
                          navigation={true}
                          loop={false}
                          breakpoints={{
                            640: {
                              slidesPerView: 5.5,
                            },
                            768: {
                              slidesPerView: 5.5,
                            },
                            1024: {
                              slidesPerView: 10,
                            },
                          }}
                        >
                          <div
                            role="list"
                            className="inline-flex mx-4 space-x-0 sm:mx-0 lg:mx-0 lg:space-x-0 lg:grid lg:grid-cols-4 lg:gap-x-0 empty-msg"
                          >
                            {reviewImages?.null?.map((img: any) =>
                              img?.images?.map(
                                (allImages: any, allid: number) => (
                                  <SwiperSlide
                                    className="px-0 image-container"
                                    key={`product-${allid}-swiper-key`}
                                  >
                                    <div className="px-1 pt-1 border border-gray-200">
                                      <img
                                        src={allImages?.name}
                                        width={50}
                                        height={50}
                                        className="object-cover mt-1 rounded"
                                        alt="Check"
                                      />
                                    </div>
                                  </SwiperSlide>
                                )
                              )
                            )}
                          </div>
                        </Swiper>
                      ) : (
                        <div className="flex justify-start gap-2 empty-msg">
                          {reviewImages?.null?.map((img: any) =>
                            img?.images?.map(
                              (allImages: any, allid: number) => (
                                <div
                                  key={`product-${allid}-rating-images`}
                                  className="px-1 pt-1 border border-gray-200"
                                >
                                  <img
                                    placeholder="blur"
                                    className="object-cover rounded h-d-50 w-d-50"
                                    src={allImages?.name}
                                    alt="img"
                                  />
                                </div>
                              )
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

              {/* CUSTOMER REVIEW */}
              <div
                className={classNames(
                  reviews != null ? 'pt-4 mt-4 border-t border-gray-200' : '',
                  'flex flex-col '
                )}
              >
                {reviews != null ? (
                  <>
                    <h3 className="mb-2 text-sm font-semibold text-secondary-full-opacity">
                      Customer Reviews ({reviews?.totalRecord})
                    </h3>
                  </>
                ) : (
                  <></>
                  // <><h3 className="mb-2 text-sm font-semibold text-secondary-full-opacity">Customer Reviews (0)</h3></>
                )}

                {reviews?.productReviews
                  ?.slice(0, isAllReview)
                  ?.map((review: any, rid: number) => (
                    <div
                      className="flex flex-col pb-4 mb-4 border-b border-gray-200"
                      key={rid}
                    >
                      <div className="flex justify-between">
                        <div className="item-left">
                          <h4 className="text-sm font-normal text-primary">
                            {review?.nickName}
                          </h4>
                        </div>
                        <div className="item-right">
                          <h4 className="text-xs font-light text-brown-semilight">
                            {formatDate(
                              new Date(review?.postedOn),
                              DATE_FORMAT
                            )}
                          </h4>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <div className="item-left">
                          {[0, 1, 2, 3, 4].map((rating) => (
                            <Star
                              key={rating}
                              className={classNames(
                                review.rating > rating
                                  ? 'text-orange-500'
                                  : 'text-gray-200',
                                'h-5 w-5 flex-shrink-0 inline-block'
                              )}
                              aria-hidden="true"
                            />
                          ))}
                        </div>
                        <div className="item-right">
                          <h4 className="text-sm font-medium text-brown">
                            <img
                              src={`${IMAGE_ASSET_BASE_URL}/icons/double-check.svg`}
                              className="relative inline mr-1 top-1"
                              width={14}
                              height={14}
                              alt="Check"
                            />
                            <span className="relative inline-block pl-1 text-xs font-light -top-0.5 text-brown-semilight">
                              Verified Purchase
                            </span>
                          </h4>
                        </div>
                      </div>
                      <div className="flex flex-col mt-2">
                        <h3 className="mb-2 font-semibold text-primary text-md">
                          {review?.title}
                        </h3>
                        <div
                          className="text-sm font-light text-primary"
                          dangerouslySetInnerHTML={{
                            __html: review?.comment,
                          }}
                        ></div>
                      </div>
                      {review?.images.length > 0 && (
                        <div className="flex justify-start gap-2 mt-2">
                          {!NEXT_PUBLIC_CUSTOMER_REVIEW_IMAGES_DISABLED &&
                            review?.images?.map((rimg: any, riid: number) => (
                              <>
                                <div
                                  key={`produc-${riid}-rating-viewer`}
                                  className="px-1 pt-1 border border-gray-200"
                                >
                                  <img
                                    placeholder="blur"
                                    className="object-cover border border-gray-200 rounded h-d-50 w-d-50"
                                    src={rimg?.name}
                                    alt="img"
                                  />
                                  {/* <Image
                                    priority={true} src={rimg?.name} width={50} height={50} layout="fixed" className="object-cover border border-gray-200 rounded" /> */}
                                </div>
                              </>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              {reviews?.productReviews?.length > 3 && isAllReview == 3 && (
                <div className="flex flex-col justify-start text-left group">
                  <Link
                    legacyBehavior
                    href={`/reviews/${product.link}/${product.recordId}`}
                    passHref
                  >
                    <span
                      className="text-sm font-semibold text-left text-orange-500 group-hover:text-orange-700"
                      onClick={() => readWriteReview()}
                    >
                      Read all Reviews ({reviews?.totalRecord})
                      <ArrowRight className="inline w-4 h-4 ml-1 text-orange-500 group-hover:text-orange-700" />
                    </span>
                  </Link>
                </div>
              )}
              {reviews?.productReviews?.length > 3 && isAllReview > 3 && (
                <div className="flex flex-col justify-start group">
                  <button
                    className="text-sm font-semibold text-left text-orange-500 group-hover:text-orange-700"
                    onClick={() => setAllReview(3)}
                  >
                    Read less Reviews
                    <ArrowRight className="inline w-4 h-4 ml-1 text-orange-500 group-hover:text-orange-700" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <Transition.Root show={isSubmitReview} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-50 overflow-hidden"
            onClose={() => setSubmitReview(false)}
          >
            <div className="absolute inset-0 overflow-hidden side-overlay bg-black/[.5]">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-400"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-400"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Overlay className="" />
              </Transition.Child>

              <div className="fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-400"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-400"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <div className="w-screen sm:max-w-md">
                    <div className="flex flex-col h-full overflow-x-hidden overflow-y-auto bg-white shadow-xl custom-scroll">
                      <div className="sticky top-0 z-50 flex flex-col w-full pt-5 bg-white border-b border-gray-200">
                        <div className="flex items-start justify-start px-4 pb-3 sm:px-8">
                          <Dialog.Title className="text-lg font-medium text-gray-900">
                            <button
                              type="button"
                              className="p-0 mr-5 text-black hover:text-black"
                              onClick={() => setSubmitReview(false)}
                            >
                              <span className="sr-only">{CLOSE_PANEL}</span>
                              <Cross
                                className="hidden w-6 h-6 sm:block"
                                aria-hidden="true"
                              />
                              <ArrowLeft
                                className="block w-6 h-6 sm:hidden"
                                aria-hidden="true"
                              />
                            </button>
                          </Dialog.Title>
                          <div className="relative -top-1">
                            <span className="text-xl font-bold dark:text-black">
                              Rate & Review
                            </span>
                          </div>
                        </div>
                      </div>

                      <ReviewInput
                        data={product}
                        productId={product?.productId ?? product?.recordId}
                        setSubmitReview={setSubmitReview}
                        deviceInfo={deviceInfo}
                      />
                      {/* <Rating /> */}
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </div>
    </>
  )
}
